import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Copy, Filter, Gift, Landmark, Mail, Smartphone } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const toDateInputValue = (date) => date.toISOString().slice(0, 10);

const formatAmount = (value) => Number(value || 0).toFixed(2);

const PENDING_WITHDRAWAL_STATUSES = new Set(['pending']);
const PROCESSED_WITHDRAWAL_STATUSES = new Set(['success', 'successful', 'processed', 'completed', 'failed', 'rejected']);

const normalizeStatus = (status) => String(status || '').trim().toLowerCase();
const isPendingWithdrawal = (item) => PENDING_WITHDRAWAL_STATUSES.has(normalizeStatus(item?.status));
const isProcessedWithdrawal = (item) => PROCESSED_WITHDRAWAL_STATUSES.has(normalizeStatus(item?.status));
const getWithdrawalHistoryDate = (item) => item?.processedAt || item?.updatedAt || item?.createdAt;


const AdminWithdrawalsSection = () => {
  const {
    adminWithdrawals,
    withdrawalStats,
    refreshAdminWithdrawals,
    updateAdminWithdrawalStatus,
  } = useAuth();

  const [requestTab, setRequestTab] = useState('pending');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [processingId, setProcessingId] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [copiedKey, setCopiedKey] = useState('');

  const activeFilters = useMemo(() => {
    const filters = {};
    if (fromDate) filters.fromDate = fromDate;
    if (toDate) filters.toDate = toDate;
    return filters;
  }, [fromDate, toDate]);

  const sourceWithdrawals = useMemo(
    () => adminWithdrawals,
    [adminWithdrawals],
  );

  const filteredSourceWithdrawals = useMemo(() => {
    let filtered = [...sourceWithdrawals];

    if (fromDate) {
      const fromStart = new Date(`${fromDate}T00:00:00`);
      filtered = filtered.filter((item) => {
        const targetDate = isProcessedWithdrawal(item) ? getWithdrawalHistoryDate(item) : item?.createdAt;
        return targetDate && new Date(targetDate) >= fromStart;
      });
    }

    if (toDate) {
      const toEnd = new Date(`${toDate}T23:59:59.999`);
      filtered = filtered.filter((item) => {
        const targetDate = isProcessedWithdrawal(item) ? getWithdrawalHistoryDate(item) : item?.createdAt;
        return targetDate && new Date(targetDate) <= toEnd;
      });
    }

    return filtered;
  }, [sourceWithdrawals, fromDate, toDate]);

  const displayedWithdrawals = useMemo(() => {
    if (requestTab === 'pending') {
      return filteredSourceWithdrawals.filter(isPendingWithdrawal);
    }
    return filteredSourceWithdrawals.filter(isProcessedWithdrawal);
  }, [filteredSourceWithdrawals, requestTab]);

  const statsToDisplay = useMemo(
    () => withdrawalStats,
    [withdrawalStats],
  );

  const fetchWithdrawals = async (filters = activeFilters) => {
    setLoading(true);
    setInfoMessage('');
    const result = await refreshAdminWithdrawals(filters);
    if (!result.success) {
      setInfoMessage(result.error || 'Failed to load withdrawals');
    }
    setLoading(false);
    return result;
  };

  useEffect(() => {
    fetchWithdrawals({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setPresetRange = async (days) => {
    const now = new Date();
    const to = toDateInputValue(now);
    const fromDateObj = new Date(now);
    fromDateObj.setDate(now.getDate() - (days - 1));
    const from = toDateInputValue(fromDateObj);

    setFromDate(from);
    setToDate(to);

    await fetchWithdrawals({ fromDate: from, toDate: to });
  };

  const resetFilters = async () => {
    setFromDate('');
    setToDate('');

    await fetchWithdrawals({});
  };

  const handleCopy = async (key, value) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(String(value));
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(''), 1200);
    } catch {
      setInfoMessage('Copy failed. Please copy manually.');
    }
  };

  const handleAction = async (withdrawal, action) => {
    let reason = '';
    if (action === 'reject') {
      const input = window.prompt('Enter rejection reason:', 'Rejected by admin');
      if (input === null) return;
      reason = input.trim() || 'Rejected by admin';
    }

    if (action === 'wrong_upi') {
      const input = window.prompt('Reason:', 'Wrong UPI ID');
      if (input === null) return;
      reason = input.trim() || 'Wrong UPI ID';
    }

    if (action === 'wrong_bank') {
      const input = window.prompt('Reason:', 'Wrong bank details');
      if (input === null) return;
      reason = input.trim() || 'Wrong bank details';
    }

    setProcessingId(withdrawal.id);
    const payload = {
      action,
      ...(reason ? { reason } : {}),
    };

    const result = await updateAdminWithdrawalStatus(withdrawal.id, payload, activeFilters);
    if (!result.success) {
      setInfoMessage(result.error || 'Failed to update withdrawal status');
    } else {
      setInfoMessage('Withdrawal updated successfully.');
    }
    setProcessingId('');
  };

  return (
    <motion.div
      className="glass-panel rounded-[18px] p-3 space-y-3"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 }}
    >
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-base font-bold text-[#000000] font-['Outfit']">Withdrawals</h2>
        <button
          onClick={() => fetchWithdrawals(activeFilters)}
          className="text-[11px] rounded-full border border-[#9333EA]/30 px-2.5 py-1 text-[#9333EA] font-semibold"
        >
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
        <div className="rounded-[10px] bg-white/70 border border-[#ececec] px-2 py-1.5">
          <p className="text-[10px] text-[#666666] font-['DM_Sans'] leading-tight">Pending</p>
          <p className="text-sm font-bold text-orange-600 font-['Outfit']">{formatAmount(statsToDisplay.totalPendingAmount)}</p>
        </div>
        <div className="rounded-[10px] bg-white/70 border border-[#ececec] px-2 py-1.5">
          <p className="text-[10px] text-[#666666] font-['DM_Sans'] leading-tight">All time</p>
          <p className="text-sm font-bold text-green-600 font-['Outfit']">{formatAmount(statsToDisplay.totalAmountGivenAllTime)}</p>
        </div>
        <div className="rounded-[10px] bg-white/70 border border-[#ececec] px-2 py-1.5">
          <p className="text-[10px] text-[#666666] font-['DM_Sans'] leading-tight">Yesterday</p>
          <p className="text-sm font-bold text-[#111111] font-['Outfit']">{formatAmount(statsToDisplay.totalAmountGivenYesterday)}</p>
        </div>
        <div className="rounded-[10px] bg-white/70 border border-[#ececec] px-2 py-1.5">
          <p className="text-[10px] text-[#666666] font-['DM_Sans'] leading-tight">Last week</p>
          <p className="text-sm font-bold text-[#111111] font-['Outfit']">{formatAmount(statsToDisplay.totalAmountGivenLastWeek)}</p>
        </div>
      </div>

      <div className="rounded-[10px] bg-white/70 border border-[#ececec] p-2 space-y-2">
        <div className="flex items-center gap-1 text-[11px] text-[#666666] font-['DM_Sans']">
          <Filter className="w-3 h-3" /> Filters
        </div>
        <div className="grid grid-cols-2 gap-1.5">
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="rounded-[8px] border border-[#ececec] px-2 py-1.5 text-[11px]"
          />
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="rounded-[8px] border border-[#ececec] px-2 py-1.5 text-[11px]"
          />
        </div>
        <div className="grid grid-cols-2 gap-1.5">
          <button
            onClick={() => fetchWithdrawals(activeFilters)}
            className="rounded-full bg-[#9333EA] text-white text-[11px] font-semibold py-1.5"
          >
            Apply
          </button>
          <button
            onClick={resetFilters}
            className="rounded-full border border-[#9333EA]/30 text-[#9333EA] text-[11px] font-semibold py-1.5"
          >
            Reset
          </button>
        </div>
        <div className="grid grid-cols-2 gap-1.5">
          <button
            onClick={() => setPresetRange(1)}
            className="rounded-full border border-[#ececec] text-[#333333] text-[11px] font-semibold py-1.5"
          >
            Today
          </button>
          <button
            onClick={() => setPresetRange(7)}
            className="rounded-full border border-[#ececec] text-[#333333] text-[11px] font-semibold py-1.5"
          >
            Last 7 days
          </button>
        </div>
      </div>

      <div className="rounded-[10px] bg-white/70 border border-[#ececec] p-1">
        <div className="grid grid-cols-2 gap-1">
          <button
            onClick={() => setRequestTab('pending')}
            className={`py-1.5 rounded-[8px] text-xs font-semibold transition-colors ${
              requestTab === 'pending'
                ? 'bg-gradient-to-r from-[#FACC15] to-[#EAB308] text-[#3F2A00] shadow-sm'
                : 'text-[#666666] bg-white border border-[#FDE68A]'
            }`}
          >
            Pending / Fresh
          </button>
          <button
            onClick={() => setRequestTab('processed')}
            className={`py-1.5 rounded-[8px] text-xs font-semibold transition-colors ${
              requestTab === 'processed'
                ? 'bg-gradient-to-r from-[#FACC15] to-[#EAB308] text-[#3F2A00] shadow-sm'
                : 'text-[#666666] bg-white border border-[#FDE68A]'
            }`}
          >
            Processed
          </button>
        </div>
      </div>

      {infoMessage && (
        <p className="rounded-[10px] bg-white/80 px-2 py-1.5 text-[11px] text-[#333333] font-['DM_Sans']">
          {infoMessage}
        </p>
      )}

      <div className="space-y-2">
        {loading ? (
          <div className="rounded-[10px] bg-white/70 border border-[#ececec] p-2 text-[11px] text-[#666666] font-['DM_Sans']">
            Loading withdrawal requests...
          </div>
        ) : displayedWithdrawals.length === 0 ? (
          <div className="rounded-[10px] bg-white/70 border border-[#ececec] p-2 text-[11px] text-[#666666] font-['DM_Sans']">
            {requestTab === 'pending' ? 'No pending withdrawal requests.' : 'No processed withdrawal requests.'}
          </div>
        ) : (
          displayedWithdrawals.map((withdrawal) => {
            const status = normalizeStatus(withdrawal.status || 'pending');
            const isPending = isPendingWithdrawal(withdrawal);
            const statusClass = ['success', 'successful', 'processed', 'completed'].includes(status)
              ? 'bg-green-100 text-green-700'
              : ['rejected', 'failed'].includes(status)
                ? 'bg-red-100 text-red-700'
                : 'bg-yellow-100 text-yellow-700';

            return (
              <div key={withdrawal.id} className="rounded-[12px] border border-[#ececec] bg-white/80 p-2 space-y-1.5">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-[13px] font-semibold text-[#111111] font-['Outfit']">{withdrawal.userName || 'Unknown user'}</p>
                    <p className="text-[11px] text-[#666666] font-['DM_Sans']">{withdrawal.username || withdrawal.userId}</p>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full capitalize ${statusClass}`}>{status}</span>
                </div>

                <div className="rounded-[8px] bg-[#f7f7f7] p-1.5 text-[11px] font-['DM_Sans']">
                  <p className="text-[#777777]">Final Amount (after 10% cut)</p>
                  <p className="font-semibold text-[#111111]">{formatAmount(withdrawal.finalAmount)}</p>
                </div>

                <p className="text-[11px] text-[#666666] font-['DM_Sans']">{new Date(isPending ? withdrawal.createdAt : getWithdrawalHistoryDate(withdrawal)).toLocaleString()}</p>

                {withdrawal.method === 'upi' ? (
                  <div className="rounded-[8px] bg-[#f7f7f7] p-1.5 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <Smartphone className="w-3 h-3 text-[#666666]" />
                      <p className="text-[11px] text-[#111111] truncate">{withdrawal.upiId || 'NA'}</p>
                    </div>
                    <button
                      onClick={() => handleCopy(`${withdrawal.id}-upi`, withdrawal.upiId)}
                      className="text-[#9333EA]"
                    >
                      {copiedKey === `${withdrawal.id}-upi` ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                ) : withdrawal.method === 'giftcard' ? (
                  <div className="rounded-[8px] bg-[#f7f7f7] p-1.5 space-y-1">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <Gift className="w-3 h-3 text-[#9333EA]" />
                      <p className="text-[11px] text-[#111111] truncate">
                        <span className="text-[#666666]">Gift Card:</span>{' '}
                        <span className="capitalize">{(withdrawal.rewardType || '').replace('_', ' ') || 'NA'}</span>
                      </p>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <Mail className="w-3 h-3 text-[#666666]" />
                        <p className="text-[11px] text-[#111111] truncate">{withdrawal.deliveryEmail || 'NA'}</p>
                      </div>
                      <button
                        onClick={() => handleCopy(`${withdrawal.id}-email`, withdrawal.deliveryEmail)}
                        className="text-[#9333EA]"
                      >
                        {copiedKey === `${withdrawal.id}-email` ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-[8px] bg-[#f7f7f7] p-1.5 space-y-1">
                    {[
                      ['Name', withdrawal.bankDetails?.accountHolderName, 'name'],
                      ['Account', withdrawal.bankDetails?.accountNumber, 'account'],
                      ['IFSC', withdrawal.bankDetails?.ifscCode, 'ifsc'],
                    ].map(([label, value, key]) => (
                      <div key={key} className="flex items-center justify-between gap-2">
                        <p className="text-[11px] text-[#111111] min-w-0 truncate">
                          <span className="text-[#666666]">{label}:</span> {value || 'NA'}
                        </p>
                        <button
                          onClick={() => handleCopy(`${withdrawal.id}-${key}`, value)}
                          className="text-[#9333EA]"
                        >
                          {copiedKey === `${withdrawal.id}-${key}` ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                    ))}
                    <div className="flex items-center gap-1 text-[10px] text-[#666666] pt-0.5">
                      <Landmark className="w-3 h-3" /> Bank transfer
                    </div>
                  </div>
                )}

                {withdrawal.reason && (
                  <p className="text-[11px] text-red-600 font-['DM_Sans']">Reason: {withdrawal.reason}</p>
                )}

                {isPending && (
                  <div className="flex items-center gap-2">
                    <label className="text-[11px] text-[#666666] font-['DM_Sans']">Action</label>
                    <select
                      defaultValue=""
                      onChange={(e) => {
                        const selectedAction = e.target.value;
                        if (!selectedAction) return;
                        handleAction(withdrawal, selectedAction);
                        e.target.value = '';
                      }}
                      disabled={processingId === withdrawal.id}
                      className="flex-1 rounded-full border border-[#9333EA]/30 bg-white px-3 py-1.5 text-[11px] font-semibold text-[#9333EA] disabled:opacity-50"
                    >
                      <option value="">Process request</option>
                      <option value="success">Success</option>
                      <option value="reject">Reject</option>
                      <option value="wrong_upi">Wrong UPI</option>
                      <option value="wrong_bank">Wrong Bank</option>
                    </select>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </motion.div>
  );
};

export default AdminWithdrawalsSection;
